// src/components/AppInformePedidosReactTable.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";


import { getReporteVentas } from "../helpers/rutaVentas";
import { getOrdenesPedido } from "../helpers/rutaOrdenPedido";

const clean = (v) => String(v ?? "").trim();

const normalizarTexto = (valor = "") =>
  String(valor || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const compararTexto = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), "es", {
    numeric: true,
    sensitivity: "base",
  });

const obtenerZonaLogisticaPorProvincia = (provincia = "") => {
  const p = normalizarTexto(provincia);

  const mapa = {
    "LA HABANA": "PUERTO DEL MARIEL",
    "PINAR DEL RIO": "PUERTO DEL MARIEL",
    ARTEMISA: "PUERTO DEL MARIEL",
    MAYABEQUE: "PUERTO DEL MARIEL",
    MATANZAS: "PUERTO DEL MARIEL",

    CIENFUEGOS: "ZONA CENTRAL",
    "VILLA CLARA": "ZONA CENTRAL",
    "SANCTI SPIRITUS": "ZONA CENTRAL",
    "CIEGO DE AVILA": "ZONA CENTRAL",
    CAMAGUEY: "ZONA CENTRAL",
    "LAS TUNAS": "ZONA CENTRAL",

    "SANTIAGO DE CUBA": "SANTIAGO DE CUBA",
    GRANMA: "SANTIAGO DE CUBA",
    HOLGUIN: "SANTIAGO DE CUBA",
    GUANTANAMO: "SANTIAGO DE CUBA",
  };

  return mapa[p] || "";
};

const fechaInputHoy = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

const fechaInputInicioMes = () => {
  const d = new Date();
  const inicio = new Date(d.getFullYear(), d.getMonth(), 1);
  return inicio.toISOString().slice(0, 10);
};

const formatoFecha = (fecha) => {
  if (!fecha) return "";

  const d = new Date(fecha);

  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("es-AR");
};

const armarNombreReceptor = (receptor = {}) => {
  const nombreCompleto = clean(receptor.nombreCompleto);

  if (nombreCompleto) return nombreCompleto;

  return [
    clean(receptor.nombre),
    clean(receptor.segundoNombre),
    clean(receptor.apellido1),
    clean(receptor.apellido2),
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
};

const armarNombreClienteCompra = (cliente = {}) => {
  const nombreCompleto = clean(cliente.nombreCompleto);

  if (nombreCompleto) return nombreCompleto;

  const nombreArmado = [
    clean(cliente.nombre),
    clean(cliente.apellido1 || cliente.apellido),
    clean(cliente.apellido2),
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return nombreArmado || clean(cliente.razonsocial) || "";
};

const armarDireccionCompleta = (receptor = {}) => {
  const direccionCompleta = clean(receptor.direccionCompleta);

  if (direccionCompleta) return direccionCompleta;

  return [
    clean(receptor.direccion),
    clean(receptor.referenciaDireccion),
    clean(receptor.municipio),
    clean(receptor.provincia),
    clean(receptor.pais) || "Cuba",
  ]
    .filter(Boolean)
    .join(" - ")
    .replace(/\s+/g, " ")
    .trim();
};

const normalizarFilaPedido = (fila = {}, index = 0) => {
  const receptor =
    fila.clienteRecibe && typeof fila.clienteRecibe === "object"
      ? fila.clienteRecibe
      : {};

  const clienteCompra =
    fila.clienteCompra && typeof fila.clienteCompra === "object"
      ? fila.clienteCompra
      : fila.cliente && typeof fila.cliente === "object"
      ? fila.cliente
      : {};

  const articulo =
    normalizarTexto(fila.descripcionProducto) ||
    normalizarTexto(fila.modeloProducto) ||
    normalizarTexto(fila.producto?.descripcion) ||
    normalizarTexto(fila.producto?.modelo) ||
    "";

  const provincia = normalizarTexto(receptor.provincia);
  const municipio = normalizarTexto(receptor.municipio);

  const zonaLogistica =
    normalizarTexto(receptor.zonaLogistica) ||
    obtenerZonaLogisticaPorProvincia(receptor.provincia);

    const nroventaTexto = clean(fila.nroventa);
    const trackReal = clean(fila.track);

    const ventaId = clean(fila.ventaId);
    const itemId = clean(fila.itemId);

    const pedidoKey =
      clean(fila.pedidoKey) || (ventaId && itemId ? `${ventaId}:${itemId}` : "");

  // Excepción temporal:
  // ventas viejas sin track real se muestran con un track visual derivado del nroventa.
  // Cuando el backend devuelva track real, se usa el real automáticamente.
  const track =
    trackReal || (nroventaTexto ? `ETR-${nroventaTexto.padStart(6, "0")}` : "");

  return {
    id: `${ventaId || "venta"}-${itemId || index}`,

    pedidoKey,
    ventaId,
    itemId,

    nroventa: nroventaTexto,
    track,
    trackReal,

    fecha: fila.fecha || "",
    fechaTexto: formatoFecha(fila.fecha),

    articulo,
    cantidad: num(fila.cantidad),

    clienteUSA: armarNombreClienteCompra(clienteCompra),

    nombre: armarNombreReceptor(receptor),
    identificacion: clean(receptor.carnetIdentidad),

    provincia,
    municipio,
    zonaLogistica,

    repartoBarrioPoblado:
      clean(receptor.repartoBarrioPoblado) ||
      clean(receptor.barrio) ||
      clean(receptor.reparto) ||
      clean(receptor.referenciaDireccion),

    direccionCompleta: armarDireccionCompleta(receptor),
    telefono: clean(receptor.telefonoPrincipal),

    estadoVenta: clean(fila.estadoVenta),
    estadoItem: clean(fila.estadoItem),
  };
};

const getOpciones = (filas, campo) => {
  const set = new Set();

  filas.forEach((fila) => {
    const valor = clean(fila[campo]);
    if (valor) set.add(valor);
  });

  return Array.from(set).sort(compararTexto);
};

const agruparTotales = (filas, campo) => {
  const mapa = new Map();

  filas.forEach((fila) => {
    const key = clean(fila[campo]) || "SIN DATO";
    const actual = mapa.get(key) || {
      nombre: key,
      pedidos: 0,
      unidades: 0,
    };

    actual.pedidos += 1;
    actual.unidades += num(fila.cantidad);

    mapa.set(key, actual);
  });

  return Array.from(mapa.values()).sort((a, b) => {
    if (b.unidades !== a.unidades) return b.unidades - a.unidades;
    return compararTexto(a.nombre, b.nombre);
  });
};

const exportarCSV = (filas) => {
  const headers = [
    "Fecha pedido cliente",
    "Artículo",
    "Cantidad",
    "Cliente USA",
    "Nombre receptor",
    "Identificación",
    "Provincia",
    "Municipio",
    "Zona logística",
    "Reparto/Barrio/Poblado",
    "Dirección completa",
    "Teléfono",
    "Track",
    "Nro venta",
  ];

  const values = filas.map((fila) => [
    fila.fechaTexto,
    fila.articulo,
    fila.cantidad,
    fila.clienteUSA,
    fila.nombre,
    fila.identificacion,
    fila.provincia,
    fila.municipio,
    fila.zonaLogistica,
    fila.repartoBarrioPoblado,
    fila.direccionCompleta,
    fila.telefono,
    fila.track,
    fila.nroventa,
  ]);

  const escapar = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

  const csv = [
    headers.map(escapar).join(";"),
    ...values.map((row) => row.map(escapar).join(";")),
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = `informe-pedidos-${fechaInputHoy()}.csv`;
  a.click();

  URL.revokeObjectURL(url);
};

const exportarPDF = ({ filas, totales, desde, hasta }) => {
  const doc = new jsPDF("landscape", "mm", "a4");

  doc.setFontSize(14);
  doc.text("Informe de Pedidos", 10, 12);

  doc.setFontSize(9);
  doc.text(`Desde: ${desde || "-"}   Hasta: ${hasta || "-"}`, 10, 19);
  doc.text(
    `Pedidos: ${totales.totalPedidos}   Unidades: ${totales.totalUnidades}`,
    10,
    25
  );

  const head = [
    [
      "Fecha",
      "Artículo",
      "Cant.",
      "Receptor",
      "Identificación",
      "Provincia",
      "Municipio",
      "Zona",
      "Barrio/Reparto",
      "Dirección",
      "Teléfono",
      "Track",
    ],
  ];

  const body = filas.map((fila) => [
    fila.fechaTexto,
    fila.articulo,
    fila.cantidad,
    fila.nombre,
    fila.identificacion,
    fila.provincia,
    fila.municipio,
    fila.zonaLogistica,
    fila.repartoBarrioPoblado,
    fila.direccionCompleta,
    fila.telefono,
    fila.track,
  ]);

  autoTable(doc, {
    startY: 31,
    head,
    body,
    theme: "grid",
    styles: {
      fontSize: 6.5,
      cellPadding: 1.2,
      overflow: "linebreak",
    },
    headStyles: {
      fontSize: 6.5,
    },
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 28 },
      2: { cellWidth: 9, halign: "right" },
      3: { cellWidth: 35 },
      4: { cellWidth: 24 },
      5: { cellWidth: 24 },
      6: { cellWidth: 25 },
      7: { cellWidth: 25 },
      8: { cellWidth: 28 },
      9: { cellWidth: 55 },
      10: { cellWidth: 22 },
      11: { cellWidth: 22 },
    },
    margin: {
      left: 5,
      right: 5,
    },
  });

  doc.save(`informe-pedidos-${fechaInputHoy()}.pdf`);
};

function ResumenCard({ titulo, valor, detalle }) {
  return (
    <Card className="shadow-sm h-100">
      <Card.Body>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {titulo}
        </div>

        <div style={{ fontSize: 24, fontWeight: 700 }}>
          {valor}
        </div>

        {!!detalle && (
          <div className="text-muted" style={{ fontSize: 12 }}>
            {detalle}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function TablaTotales({ titulo, filas }) {
  return (
    <Card className="shadow-sm h-100">
      <Card.Header>
        <b>{titulo}</b>
      </Card.Header>

      <Card.Body style={{ padding: 0 }}>
        <Table size="sm" bordered hover responsive className="mb-0">
          <thead>
            <tr>
              <th>Concepto</th>
              <th className="text-end">Pedidos</th>
              <th className="text-end">Unidades</th>
            </tr>
          </thead>

          <tbody>
            {!filas.length && (
              <tr>
                <td colSpan="3" className="text-center text-muted">
                  Sin datos
                </td>
              </tr>
            )}

            {filas.slice(0, 8).map((fila) => (
              <tr key={fila.nombre}>
                <td>{fila.nombre}</td>
                <td className="text-end">{fila.pedidos}</td>
                <td className="text-end">{fila.unidades}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

const ORDENES_DISPONIBLES = [
  { campo: "zonaLogistica", label: "Zona logística" },
  { campo: "provincia", label: "Provincia" },
  { campo: "municipio", label: "Municipio" },
  { campo: "articulo", label: "Producto" },
  { campo: "nombre", label: "Nombre receptor" },
  { campo: "fecha", label: "Fecha" },
];

export default function AppInformePedidosReactTable() {
  const [desde, setDesde] = useState(fechaInputInicioMes());
  const [hasta, setHasta] = useState(fechaInputHoy());

  const [q, setQ] = useState("");
  const [provincia, setProvincia] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [zonaLogistica, setZonaLogistica] = useState("");
  const [producto, setProducto] = useState("");

  const [ordenes, setOrdenes] = useState([
    "zonaLogistica",
    "provincia",
    "municipio",
  ]);

  const [filasBase, setFilasBase] = useState([]);
  const [ordenesPedidoGeneradas, setOrdenesPedidoGeneradas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [data, dataOrdenes] = await Promise.all([
        getReporteVentas({
          desde,
          hasta,
        }),

        getOrdenesPedido({
          estado: "GENERADA",
        }),
      ]);

      const filasApi = Array.isArray(data?.filas) ? data.filas : [];

      const normalizadas = filasApi.map((fila, index) =>
        normalizarFilaPedido(fila, index)
      );

      setFilasBase(normalizadas);

      setOrdenesPedidoGeneradas(
        Array.isArray(dataOrdenes?.ordenes) ? dataOrdenes.ordenes : []
      );
    } catch (err) {
      console.error("❌ Error al cargar informe de pedidos:", err);

      const mensaje =
        err?.response?.data?.mensaje ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "No se pudo cargar el informe de pedidos.";

      setError(mensaje);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const provinciasOpts = useMemo(
    () => getOpciones(filasBase, "provincia"),
    [filasBase]
  );

  const municipiosOpts = useMemo(
    () => getOpciones(filasBase, "municipio"),
    [filasBase]
  );

  const zonasOpts = useMemo(
    () => getOpciones(filasBase, "zonaLogistica"),
    [filasBase]
  );

  const productosOpts = useMemo(
    () => getOpciones(filasBase, "articulo"),
    [filasBase]
  );

  const filasFiltradas = useMemo(() => {
    const nq = normalizarTexto(q);
    const nProvincia = normalizarTexto(provincia);
    const nMunicipio = normalizarTexto(municipio);
    const nZona = normalizarTexto(zonaLogistica);
    const nProducto = normalizarTexto(producto);

    return filasBase.filter((fila) => {
      if (nProvincia && normalizarTexto(fila.provincia) !== nProvincia) {
        return false;
      }

      if (nMunicipio && normalizarTexto(fila.municipio) !== nMunicipio) {
        return false;
      }

      if (nZona && normalizarTexto(fila.zonaLogistica) !== nZona) {
        return false;
      }

      if (nProducto && normalizarTexto(fila.articulo) !== nProducto) {
        return false;
      }

      if (nq) {
        const textoBusqueda = normalizarTexto(
          [
            fila.clienteUSA,
            fila.nombre,
            fila.identificacion,
            fila.telefono,
            fila.track,
            fila.articulo,
            fila.provincia,
            fila.municipio,
            fila.zonaLogistica,
            fila.direccionCompleta,
            fila.nroventa,
          ].join(" ")
        );

        if (!textoBusqueda.includes(nq)) return false;
      }

      return true;
    });
  }, [filasBase, q, provincia, municipio, zonaLogistica, producto]);

  const toggleOrden = (campo) => {
    setOrdenes((prev) => {
      if (prev.includes(campo)) {
        return prev.filter((x) => x !== campo);
      }

      return [...prev, campo];
    });
  };

  const filasOrdenadas = useMemo(() => {
    const arr = [...filasFiltradas];

    arr.sort((a, b) => {
      for (const campo of ordenes) {
        let cmp = 0;

        if (campo === "fecha") {
          const fa = new Date(a.fecha).getTime() || 0;
          const fb = new Date(b.fecha).getTime() || 0;
          cmp = fa - fb;
        } else {
          cmp = compararTexto(a[campo], b[campo]);
        }

        if (cmp !== 0) return cmp;
      }

      return compararTexto(a.nombre, b.nombre);
    });

    return arr;
  }, [filasFiltradas, ordenes]);

  const mapaPedidosOrdenados = useMemo(() => {
  const mapa = new Map();

  ordenesPedidoGeneradas.forEach((orden) => {
    if (normalizarTexto(orden.estado) === "ANULADA") return;

    const info = {
      nroOrdenPedido: orden.nroOrdenPedido,
      estado: orden.estado,
      ordenId: orden._id,
    };

    (orden.items || []).forEach((item) => {
      const pedidoKey = clean(item.pedidoKey);
      const track = clean(item.track).toUpperCase();

      if (pedidoKey) {
        mapa.set(`PK:${pedidoKey}`, info);
      }

      if (track) {
        mapa.set(`TR:${track}`, info);
      }

      const ventaId = clean(item.venta?._id || item.venta);
      const itemVentaId = clean(item.itemVentaId);

      if (ventaId && itemVentaId) {
        mapa.set(`PK:${ventaId}:${itemVentaId}`, info);
      }
    });
  });

  return mapa;
}, [ordenesPedidoGeneradas]);

const getInfoOrdenFila = useCallback(
  (fila) => {
    const pedidoKey = clean(fila.pedidoKey);
    const track = clean(fila.track).toUpperCase();

    return (
      (pedidoKey && mapaPedidosOrdenados.get(`PK:${pedidoKey}`)) ||
      (track && mapaPedidosOrdenados.get(`TR:${track}`)) ||
      null
    );
  },
  [mapaPedidosOrdenados]
);

  const totales = useMemo(() => {
    const totalPedidos = filasOrdenadas.length;

    const totalUnidades = filasOrdenadas.reduce(
      (acc, fila) => acc + num(fila.cantidad),
      0
    );

    const sinTrack = filasOrdenadas.filter((fila) => !clean(fila.track)).length;

    const sinTelefono = filasOrdenadas.filter(
      (fila) => !clean(fila.telefono)
    ).length;

    const provincias = new Set(
      filasOrdenadas.map((fila) => clean(fila.provincia)).filter(Boolean)
    );

    const zonas = new Set(
      filasOrdenadas.map((fila) => clean(fila.zonaLogistica)).filter(Boolean)
    );

    return {
      totalPedidos,
      totalUnidades,
      sinTrack,
      sinTelefono,
      totalProvincias: provincias.size,
      totalZonas: zonas.size,
    };
  }, [filasOrdenadas]);

  const totalesProducto = useMemo(
    () => agruparTotales(filasOrdenadas, "articulo"),
    [filasOrdenadas]
  );

  const totalesProvincia = useMemo(
    () => agruparTotales(filasOrdenadas, "provincia"),
    [filasOrdenadas]
  );

  const totalesZona = useMemo(
    () => agruparTotales(filasOrdenadas, "zonaLogistica"),
    [filasOrdenadas]
  );

  const limpiarFiltros = () => {
    setQ("");
    setProvincia("");
    setMunicipio("");
    setZonaLogistica("");
    setProducto("");
  };

  return (
    <div className="container-fluid mt-3 mb-4">
      <style>
        {`
          .tabla-pedidos-riders tbody tr.fila-pedido-ordenado {
            --bs-table-bg: #f2df9b !important;
            --bs-table-accent-bg: #f2df9b !important;
            --bs-table-hover-bg: #f2df9b !important;
            --bs-table-color: #111827 !important;
            --bs-table-hover-color: #111827 !important;
          }

          .tabla-pedidos-riders tbody tr.fila-pedido-ordenado > td {
            background-color: #f2df9b !important;
            color: #111827 !important;
            border-color: #d8c678 !important;
            box-shadow: inset 0 0 0 9999px #f2df9b !important;
          }

          .tabla-pedidos-riders tbody tr.fila-pedido-ordenado:hover > td {
            background-color: #f2df9b !important;
            color: #111827 !important;
            border-color: #d8c678 !important;
            box-shadow: inset 0 0 0 9999px #f2df9b !important;
          }

          .tabla-pedidos-riders tbody tr.fila-pedido-ordenado > td *:not(.badge-orden-pedido) {
            color: #111827 !important;
          }

          .tabla-pedidos-riders tbody tr.fila-pedido-ordenado:hover > td *:not(.badge-orden-pedido) {
            color: #111827 !important;
          }

          .badge-orden-pedido {
            font-size: 11px;
            font-weight: 700;
            padding: 4px 7px;
            border-radius: 999px;
            background: #f6c343;
            color: #2b2b2b !important;
            white-space: nowrap;
            display: inline-block;
            box-shadow: none !important;
          }
        `}
      </style>
      <Card className="shadow-sm">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h5 className="mb-0">Informe de Pedidos</h5>
              <small className="text-muted">
                Ventas, receptores, productos y datos de logística inicial.
              </small>
            </div>

            <div className="d-flex gap-2 mt-2 mt-md-0">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={cargarDatos}
                disabled={loading}
              >
                Actualizar
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                onClick={() => exportarCSV(filasOrdenadas)}
                disabled={!filasOrdenadas.length}
              >
                Exportar CSV/Excel
              </Button>

              <Button
                variant="outline-danger"
                size="sm"
                onClick={() =>
                  exportarPDF({
                    filas: filasOrdenadas,
                    totales,
                    desde,
                    hasta,
                  })
                }
                disabled={!filasOrdenadas.length}
              >
                Exportar PDF
              </Button>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Row>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Buscar</Form.Label>
                <Form.Control
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cliente USA, Cliente receptor, CI, teléfono, track..."
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Provincia</Form.Label>
                <Form.Select
                  value={provincia}
                  onChange={(e) => setProvincia(e.target.value)}
                >
                  <option value="">Todas</option>
                  {provinciasOpts.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Municipio</Form.Label>
                <Form.Select
                  value={municipio}
                  onChange={(e) => setMunicipio(e.target.value)}
                >
                  <option value="">Todos</option>
                  {municipiosOpts.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Zona logística</Form.Label>
                <Form.Select
                  value={zonaLogistica}
                  onChange={(e) => setZonaLogistica(e.target.value)}
                >
                  <option value="">Todas</option>
                  {zonasOpts.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group className="mb-3">
                <Form.Label>Producto</Form.Label>
                <Form.Select
                  value={producto}
                  onChange={(e) => setProducto(e.target.value)}
                >
                  <option value="">Todos</option>
                  {productosOpts.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4} className="d-flex align-items-end">
              <div className="mb-3">
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={limpiarFiltros}
                >
                  Limpiar filtros
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="border rounded p-2 bg-light">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <div>
                    <b>Orden combinado</b>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Tildá en el orden que querés ordenar. Ej: Zona → Provincia → Municipio.
                    </div>
                  </div>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setOrdenes([])}
                  >
                    Limpiar orden
                  </Button>
                </div>

                <div className="mt-2">
                  {ORDENES_DISPONIBLES.map((op) => {
                    const activo = ordenes.includes(op.campo);
                    const posicion = ordenes.indexOf(op.campo) + 1;

                    return (
                      <Form.Check
                        key={op.campo}
                        inline
                        type="checkbox"
                        id={`orden-${op.campo}`}
                        checked={activo}
                        onChange={() => toggleOrden(op.campo)}
                        label={activo ? `${op.label} (${posicion})` : op.label}
                      />
                    );
                  })}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={2}>
              <ResumenCard titulo="Pedidos" valor={totales.totalPedidos} />
            </Col>

            <Col md={2}>
              <ResumenCard titulo="Unidades" valor={totales.totalUnidades} />
            </Col>

            <Col md={2}>
              <ResumenCard
                titulo="Provincias"
                valor={totales.totalProvincias}
              />
            </Col>

            <Col md={2}>
              <ResumenCard titulo="Zonas" valor={totales.totalZonas} />
            </Col>

            <Col md={2}>
              <ResumenCard titulo="Sin track" valor={totales.sinTrack} />
            </Col>

            <Col md={2}>
              <ResumenCard titulo="Sin teléfono" valor={totales.sinTelefono} />
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={4}>
              <TablaTotales titulo="Totales por producto" filas={totalesProducto} />
            </Col>

            <Col md={4}>
              <TablaTotales titulo="Totales por provincia" filas={totalesProvincia} />
            </Col>

            <Col md={4}>
              <TablaTotales titulo="Totales por zona logística" filas={totalesZona} />
            </Col>
          </Row>

          {loading && (
            <div className="text-center my-4">
              <Spinner animation="border" size="sm" />{" "}
              <span className="ms-2">Cargando pedidos...</span>
            </div>
          )}

          {!loading && (
            <div style={{ overflowX: "auto" }}>
              <Table
                bordered
                hover
                size="sm"
                responsive
                className="align-middle tabla-pedidos-riders"
              >
                <thead>
                  <tr>
                    <th>Fecha pedido cliente</th>
                    <th>Artículo</th>
                    <th className="text-end">Cantidad</th>
                    <th>Cliente USA</th>
                    <th>Nombre receptor</th>
                    <th>Identificación</th>
                    <th>Provincia</th>
                    <th>Municipio</th>
                    <th>Zona logística</th>
                    <th>Dirección completa</th>
                    <th>Teléfono</th>
                    <th>Track</th>
                    <th>Orden</th>
                  </tr>
                </thead>

                <tbody>
                  {!filasOrdenadas.length && (
                    <tr>
                      <td colSpan="13" className="text-center text-muted py-4">
                        No hay pedidos para los filtros seleccionados.
                      </td>
                    </tr>
                  )}

                  {filasOrdenadas.map((fila) => {
                    const ordenInfo = getInfoOrdenFila(fila);

                    return (
                      <tr
                        key={fila.id}
                        className={ordenInfo ? "fila-pedido-ordenado" : ""}
                      >
                        <td style={{ whiteSpace: "nowrap" }}>{fila.fechaTexto}</td>
                        <td>{fila.articulo}</td>
                        <td className="text-end">{fila.cantidad}</td>

                        <td>{fila.clienteUSA || "-"}</td>

                        <td>{fila.nombre}</td>
                        <td>{fila.identificacion}</td>
                        <td>{fila.provincia}</td>
                        <td>{fila.municipio}</td>
                        <td>{fila.zonaLogistica}</td>

                        <td style={{ minWidth: 260 }}>{fila.direccionCompleta}</td>
                        <td>{fila.telefono}</td>

                        <td style={{ whiteSpace: "nowrap" }}>
                          <b>{fila.track}</b>
                        </td>

                        <td style={{ whiteSpace: "nowrap" }}>
                          {ordenInfo ? (
                            <span className="badge-orden-pedido">
                              Orden Nº {ordenInfo.nroOrdenPedido}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
